/**
 * BLRRD Pointwise Filters — Index / Dispatcher
 *
 * Single entry point for all 9 pointwise filters. Each filter
 * exports `apply{Filter}` + `default{Filter}Params` +
 * `isIdentity{Filter}` from its own module; this file re-exports
 * them and provides a registry-style dispatch helper for the UI
 * and snapshot system.
 *
 * Pipeline-slot summary (per filter):
 *
 *   pre-kernel scene-linear  : Levels, Channel Mixer, Color Balance
 *   kernel-stage             : Curves-multi, Gradient Map
 *   post-view-transform      : Threshold, Posterize, Solarize, Sepia
 *
 * See docs/algorithms/filters-pointwise.md for the full slot
 * assignment table + citations.
 */

import {
    applyLevels, defaultLevelsParams, isIdentityLevels,
} from './levels.js';
import {
    applyCurvesMulti, defaultCurvesParams, isIdentityCurvesMulti,
    computeHistogram, buildCurveLUT, lookupLUT, evalMonotoneCubic,
} from './curves-multi.js';
import {
    applyThreshold, defaultThresholdParams, isIdentityThreshold,
} from './threshold.js';
import {
    applyPosterize, defaultPosterizeParams, isIdentityPosterize,
} from './posterize.js';
import {
    applySolarize, defaultSolarizeParams, isIdentitySolarize,
} from './solarize.js';
import {
    applySepia, defaultSepiaParams, isIdentitySepia, SEPIA_MATRIX,
} from './sepia.js';
import {
    applyChannelMixer, defaultChannelMixerParams, isIdentityChannelMixer,
    IDENTITY_MATRIX,
} from './channel-mixer.js';
import {
    applyColorBalance, defaultColorBalanceParams, isIdentityColorBalance,
    computeBandWeights, RAMP_A, RAMP_B, SCALE_OVERALL,
} from './color-balance.js';
import {
    applyGradientMap, defaultGradientMapParams, isIdentityGradientMap,
    evalGradient,
} from './gradient-map.js';

/**
 * Pipeline-slot assignment per filter. Consumers (kernel, view-
 * transform layer, UI status badge) read this to route the filter
 * to the right buffer stage.
 */
export const FILTER_SLOTS = Object.freeze({
    levels:        'pre-kernel',
    curvesMulti:   'kernel-stage',
    threshold:     'post-view-transform',
    posterize:     'post-view-transform',
    solarize:      'post-view-transform',
    sepia:         'post-view-transform',
    channelMixer:  'pre-kernel',
    colorBalance:  'pre-kernel',
    gradientMap:   'kernel-stage',
});

/**
 * Filter registry. Keys are stable filter IDs the UI references;
 * values bundle the apply / default / identity functions for
 * uniform dispatch.
 */
export const FILTERS = Object.freeze({
    levels: {
        apply: applyLevels,
        defaults: defaultLevelsParams,
        isIdentity: isIdentityLevels,
        slot: 'pre-kernel',
        label: 'Levels',
    },
    curvesMulti: {
        apply: applyCurvesMulti,
        defaults: defaultCurvesParams,
        isIdentity: isIdentityCurvesMulti,
        slot: 'kernel-stage',
        label: 'Curves (multi-point)',
    },
    threshold: {
        apply: applyThreshold,
        defaults: defaultThresholdParams,
        isIdentity: isIdentityThreshold,
        slot: 'post-view-transform',
        label: 'Threshold',
    },
    posterize: {
        apply: applyPosterize,
        defaults: defaultPosterizeParams,
        isIdentity: isIdentityPosterize,
        slot: 'post-view-transform',
        label: 'Posterize',
    },
    solarize: {
        apply: applySolarize,
        defaults: defaultSolarizeParams,
        isIdentity: isIdentitySolarize,
        slot: 'post-view-transform',
        label: 'Solarize',
    },
    sepia: {
        apply: applySepia,
        defaults: defaultSepiaParams,
        isIdentity: isIdentitySepia,
        slot: 'post-view-transform',
        label: 'Sepia',
    },
    channelMixer: {
        apply: applyChannelMixer,
        defaults: defaultChannelMixerParams,
        isIdentity: isIdentityChannelMixer,
        slot: 'pre-kernel',
        label: 'Channel Mixer',
    },
    colorBalance: {
        apply: applyColorBalance,
        defaults: defaultColorBalanceParams,
        isIdentity: isIdentityColorBalance,
        slot: 'pre-kernel',
        label: 'Color Balance',
    },
    gradientMap: {
        apply: applyGradientMap,
        defaults: defaultGradientMapParams,
        isIdentity: isIdentityGradientMap,
        slot: 'kernel-stage',
        label: 'Gradient Map',
    },
});

/**
 * Dispatch helper: apply a filter by ID.
 *
 * @param {string}        id           one of the FILTERS keys
 * @param {Float32Array}  rgba
 * @param {number}        w
 * @param {number}        h
 * @param {object}        params
 * @param {object}        [opts]
 * @returns {Float32Array}
 */
export function applyFilter(id, rgba, w, h, params, opts) {
    const f = FILTERS[id];
    if (!f) throw new Error(`applyFilter: unknown filter id "${id}"`);
    return f.apply(rgba, w, h, params, opts);
}

/* Re-export individual modules so the legacy editor / tests can
 * import either through the index or directly. */
export {
    /* levels */
    applyLevels, defaultLevelsParams, isIdentityLevels,
    /* curves-multi */
    applyCurvesMulti, defaultCurvesParams, isIdentityCurvesMulti,
    computeHistogram, buildCurveLUT, lookupLUT, evalMonotoneCubic,
    /* threshold */
    applyThreshold, defaultThresholdParams, isIdentityThreshold,
    /* posterize */
    applyPosterize, defaultPosterizeParams, isIdentityPosterize,
    /* solarize */
    applySolarize, defaultSolarizeParams, isIdentitySolarize,
    /* sepia */
    applySepia, defaultSepiaParams, isIdentitySepia, SEPIA_MATRIX,
    /* channel-mixer */
    applyChannelMixer, defaultChannelMixerParams, isIdentityChannelMixer,
    IDENTITY_MATRIX,
    /* color-balance */
    applyColorBalance, defaultColorBalanceParams, isIdentityColorBalance,
    computeBandWeights, RAMP_A, RAMP_B, SCALE_OVERALL,
    /* gradient-map */
    applyGradientMap, defaultGradientMapParams, isIdentityGradientMap,
    evalGradient,
};
